import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Typography, Avatar, Menu, MenuItem, Divider,
  CircularProgress, Container, Drawer,
  IconButton, useMediaQuery, useTheme,
  Badge, Popover, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from "@mui/material";
import MenuIcon                      from "@mui/icons-material/Menu";
import CloseIcon                     from "@mui/icons-material/Close";
import LogoutIcon                    from "@mui/icons-material/Logout";
import PersonOutlineIcon             from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon              from "@mui/icons-material/HomeOutlined";
import DashboardOutlinedIcon         from "@mui/icons-material/DashboardOutlined";
import AssignmentOutlinedIcon        from "@mui/icons-material/AssignmentOutlined";
import AddCircleOutlineIcon          from "@mui/icons-material/AddCircleOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DoneAllIcon                   from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon        from "@mui/icons-material/AssignmentTurnedIn";
import InfoOutlinedIcon              from "@mui/icons-material/InfoOutlined";
import EditNoteIcon                  from "@mui/icons-material/EditNote";
import KeyboardArrowDownIcon         from "@mui/icons-material/KeyboardArrowDown";

import echo from "../services/echo";

const API_BASE        = "";
const POLL_INTERVAL = 30_000;

function getNotifIcon(type) {
  switch (type) {
    case "procurement_status": return <AssignmentTurnedInIcon sx={{ fontSize: 18, color: "#52c41a" }} />;
    default:                   return <InfoOutlinedIcon       sx={{ fontSize: 18, color: "#8c8c8c" }} />;
  }
}

function getNotifBg(type) {
  switch (type) {
    case "procurement_status": return "#f6ffed";
    default:                   return "#f5f5f5";
  }
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return "Baru saja";
  if (diff < 3600)  return `${Math.floor(diff / 60)} mnt lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  return `${Math.floor(diff / 86400)} hari lalu`;
}

export default function UserLayout() {
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // ── Dropdown Buat Pengajuan ──
  const [buatAnchor, setBuatAnchor] = useState(null);

  // ── Drawer: Buat Pengajuan expandable ──
  const [drawerBuatOpen, setDrawerBuatOpen] = useState(false);

  // ── Dropdown Pengajuan (Sarana Prasarana + ATK) ──
  const [pengajuanAnchor, setPengajuanAnchor] = useState(null);

  const [notifAnchor,   setNotifAnchor]   = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [notifLoading,  setNotifLoading]  = useState(false);
  const pollingRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const token = localStorage.getItem("token");
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")) || null; }
    catch { return null; }
  })();
  const userId = user?.id;

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/notifications/unread-count`, { headers: authHeaders });
      const json = await res.json();
      if (json.success) setUnreadCount(json.unread_count);
    } catch (e) { console.error("Poll notif error:", e); }
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    pollingRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(pollingRef.current);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!userId) return;
    const channel = echo.private(`notifications.${userId}`);
    channel.listen(".notification.sent", (data) => {
      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((prev) => prev + 1);
      if ("Notification" in window && window.Notification.permission === "granted") {
        new window.Notification(data.title, { body: data.message, icon: "/lpkia-logo2.png" });
      }
    });
    return () => { echo.leave(`notifications.${userId}`); };
  }, [userId]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/notifications?limit=30`, { headers: authHeaders });
      const json = await res.json();
      if (json.success) { setNotifications(json.data); setUnreadCount(json.unread_count); }
    } catch (e) { console.error("Fetch notif error:", e); }
    finally { setNotifLoading(false); }
  };

  const handleNotifOpen  = (e) => { setNotifAnchor(e.currentTarget); fetchNotifications(); };
  const handleNotifClose = ()  => setNotifAnchor(null);

  const handleMarkRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await fetch(`${API_BASE}/api/notifications/${notif.id}/read`, { method: "POST", headers: authHeaders });
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) { console.error(e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE}/api/notifications/read-all`, { method: "POST", headers: authHeaders });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { console.error(e); }
  };

  const handleNotifClick = (notif) => {
    handleMarkRead(notif);
    if (notif.type === "procurement_status") {
      const id = notif.data?.procurement_id;
      navigate(id ? `/user/procurement/${id}` : "/user/procurement");
    } else if (notif.type === "atk_status") {
      const id = notif.data?.atk_request_id;
      navigate(id ? `/user/atk` : "/user/atk");
    }
    handleNotifClose();
  };

  const notifOpen = Boolean(notifAnchor);

  const userInitials = user?.name
    ? user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()
    : "U";

  const isActive = (path) => {
    if (path === "/user") return location.pathname === "/user";
    return location.pathname.startsWith(path);
  };

  // active jika salah satu sub-route pengajuan aktif
  const isPengajuanActive = isActive("/user/procurement") || isActive("/user/atk");

  const handleLogoutClick = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      await fetch(`${API_BASE}/api/logout`, { method: "POST", headers: authHeaders });
    } catch (e) {
      console.error("Logout error:", e);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setLoggingOut(false);
      setLogoutDialogOpen(false);
      navigate("/login");
    }
  };

  /* ── Notif Popover ── */
  const NotifPopover = (
    <Popover
      open={notifOpen} anchorEl={notifAnchor} onClose={handleNotifClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ elevation: 4, sx: { mt: 1.5, width: 360, borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" } }}
    >
      <Box sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f0f0f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>Notifikasi</Typography>
          {unreadCount > 0 && (
            <Chip label={unreadCount} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "#ff4d4f", color: "#fff", "& .MuiChip-label": { px: 0.8 } }} />
          )}
        </Box>
        {unreadCount > 0 && (
          <Tooltip title="Tandai semua sudah dibaca" arrow>
            <IconButton size="small" onClick={handleMarkAllRead}
              sx={{ color: "#1677ff", gap: 0.5, borderRadius: "6px", px: 1, "&:hover": { backgroundColor: "#e8f4fd" } }}>
              <DoneAllIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>Baca semua</Typography>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
        {notifLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 40, color: "#d9d9d9", mb: 1 }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#8c8c8c" }}>Tidak ada notifikasi</Typography>
          </Box>
        ) : notifications.map((notif, idx) => (
          <Box key={notif.id}>
            <Box onClick={() => handleNotifClick(notif)} sx={{
              px: 2.5, py: 1.8, display: "flex", gap: 1.5, alignItems: "flex-start",
              cursor: "pointer", position: "relative",
              backgroundColor: notif.is_read ? "#fff" : "#f0f7ff",
              "&:hover": { backgroundColor: notif.is_read ? "#fafafa" : "#e8f4fd" },
            }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "8px", flexShrink: 0, backgroundColor: getNotifBg(notif.type), display: "flex", alignItems: "center", justifyContent: "center" }}>
                {getNotifIcon(notif.type)}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: notif.is_read ? 400 : 600, color: "#111827", lineHeight: 1.3, mb: 0.3 }}>
                  {notif.title}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#595959", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {notif.message}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#bfbfbf", mt: 0.5 }}>
                  {timeAgo(notif.created_at)}
                </Typography>
              </Box>
              {!notif.is_read && <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#1677ff", flexShrink: 0, mt: 0.5 }} />}
            </Box>
            {idx < notifications.length - 1 && <Divider sx={{ mx: 2.5, borderColor: "#f5f5f5" }} />}
          </Box>
        ))}
      </Box>

      {notifications.length > 0 && (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f0f0f0", textAlign: "center", backgroundColor: "#fafafa" }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#1677ff", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
            onClick={() => { handleNotifClose(); navigate("/user/procurement"); }}>
            Lihat semua notifikasi →
          </Typography>
        </Box>
      )}
    </Popover>
  );

  /* ── Mobile Drawer ── */
  const MobileDrawer = (
    <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
      PaperProps={{ sx: { width: 260, border: "none", boxShadow: "8px 0 32px rgba(0,0,0,0.08)" } }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 2, borderBottom: "1px solid #f3f4f6" }}>
        <Link to="/user" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} onClick={() => setDrawerOpen(false)}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA" sx={{ height: 26, width: "auto", objectFit: "contain" }} />
          <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#111827" }}>BAS-LPKIA</Typography>
        </Link>
        <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: "#9ca3af", "&:hover": { backgroundColor: "#f3f4f6" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Menu */}
      <Box sx={{ py: 1 }}>
        {[
          { label: "Dashboard", path: "/user" },
          { label: "Sarana Prasarana", path: "/user/procurement" },
          { label: "ATK", path: "/user/atk" },
        ].map((item) => (
          <Box
            key={item.path}
            onClick={() => { navigate(item.path); setDrawerOpen(false); }}
            sx={{
              px: 2.5, py: 1.3, fontSize: "0.9rem", cursor: "pointer",
              fontWeight: isActive(item.path) ? 700 : 500,
              color: isActive(item.path) ? "#111827" : "#4b5563",
              backgroundColor: isActive(item.path) ? "#f3f4f6" : "transparent",
              "&:hover": { backgroundColor: "#f3f4f6" },
              transition: "all 0.1s",
            }}
          >
            {item.label}
          </Box>
        ))}

        <Divider sx={{ my: 1, borderColor: "#f3f4f6" }} />

        <Typography sx={{ px: 2.5, py: 0.8, fontSize: "0.7rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Buat Pengajuan
        </Typography>
        {[
          { label: "Sarpras Baru", path: "/user/procurement/create" },
          { label: "ATK Baru", path: "/user/atk/create" },
        ].map((item) => (
          <Box
            key={item.path}
            onClick={() => { navigate(item.path); setDrawerOpen(false); }}
            sx={{
              px: 2.5, py: 1.3, fontSize: "0.9rem", cursor: "pointer",
              fontWeight: 500, color: "#4b5563",
              "&:hover": { backgroundColor: "#f3f4f6" },
              transition: "all 0.1s",
            }}
          >
            {item.label}
          </Box>
        ))}

        <Divider sx={{ my: 1, borderColor: "#f3f4f6" }} />

        <Box onClick={() => { navigate("/user/profile"); setDrawerOpen(false); }}
          sx={{ px: 2.5, py: 1.3, fontSize: "0.9rem", cursor: "pointer", fontWeight: isActive("/user/profile") ? 700 : 500, color: isActive("/user/profile") ? "#111827" : "#4b5563", backgroundColor: isActive("/user/profile") ? "#f3f4f6" : "transparent", "&:hover": { backgroundColor: "#f3f4f6" }, transition: "all 0.1s" }}>
          Profil Saya
        </Box>
        <Box onClick={handleLogoutClick}
          sx={{ px: 2.5, py: 1.3, fontSize: "0.9rem", cursor: "pointer", fontWeight: 500, color: "#ef4444", "&:hover": { backgroundColor: "#fef2f2" }, transition: "all 0.1s" }}>
          {loggingOut ? "Logging out..." : "Keluar"}
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fafafa", display: "flex", flexDirection: "column" }}>
      {MobileDrawer}
      {NotifPopover}

      {/* ── NAVBAR ── */}
      <Box component="nav" sx={{ position: "sticky", top: 0, zIndex: 100, backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", borderBottom: "1px solid #e5e7eb", height: 64 }}>
        <Box sx={{ maxWidth: "900px", mx: "auto", px: { xs: 2, md: 3 }, height: "100%", display: "flex", alignItems: "center", gap: 1.5 }}>

          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} size="small" sx={{ color: "#374151", p: 0.5, borderRadius: "6px", "&:hover": { backgroundColor: "#f3f4f6" } }}>
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Logo */}
          <Link to="/user" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10, flexGrow: 1 }}>
            <Box component="img" src="/lpkia-logo2.png" alt="LPKIA" sx={{ height: 32, width: "auto", objectFit: "contain" }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "0.875rem", color: "#111827", lineHeight: 1.2, letterSpacing: "-0.01em" }}>BAS-LPKIA</Typography>
              <Typography sx={{ fontSize: "0.6rem", color: "#6b7280", lineHeight: 1.2 }}>Sistem Pengadaan Sarana Prasarana</Typography>
            </Box>
          </Link>

          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1.5 }}>

              {/* Dashboard */}
              <Link to="/user" style={{ textDecoration: "none" }}>
                <Box sx={{ px: 1.4, py: 0.7, borderRadius: "7px", fontSize: "0.845rem",
                  fontWeight: isActive("/user") ? 600 : 500,
                  color: isActive("/user") ? "#111827" : "#6b7280",
                  transition: "all 0.15s", "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" } }}>
                  Dashboard
                </Box>
              </Link>

              {/* ── Dropdown Pengajuan ── */}
              <Box
                onClick={(e) => setPengajuanAnchor(e.currentTarget)}
                sx={{
                  px: 1.4, py: 0.7, borderRadius: "7px", fontSize: "0.845rem",
                  fontWeight: isPengajuanActive ? 600 : 500,
                  color: isPengajuanActive ? "#111827" : "#6b7280",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 0.3,
                  transition: "all 0.15s",
                  backgroundColor: Boolean(pengajuanAnchor) ? "#f3f4f6" : "transparent",
                  "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" },
                }}
              >
                Pengajuan
                <KeyboardArrowDownIcon sx={{
                  fontSize: 15,
                  transition: "transform 0.2s",
                  transform: Boolean(pengajuanAnchor) ? "rotate(180deg)" : "rotate(0deg)",
                }} />
              </Box>

              <Menu
                anchorEl={pengajuanAnchor}
                open={Boolean(pengajuanAnchor)}
                onClose={() => setPengajuanAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    mt: 1, minWidth: 230, borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                    overflow: "hidden",
                  },
                }}
              >
                <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", px: 0.5 }}>
                    Riwayat Pengajuan
                  </Typography>
                </Box>
                <Box sx={{ p: 0.75 }}>
                  {/* Sarana Prasarana */}
                  <MenuItem
                    onClick={() => { navigate("/user/procurement"); setPengajuanAnchor(null); }}
                    sx={{
                      borderRadius: "8px", py: 1, px: 1.5, gap: 1.5,
                      backgroundColor: isActive("/user/procurement") ? "#f0f7ff" : "transparent",
                      "&:hover": { backgroundColor: "#f0f7ff" },
                    }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "8px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <AssignmentOutlinedIcon sx={{ fontSize: 17, color: "#2563eb" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600, color: "#111827", lineHeight: 1.2 }}>
                        Sarana Prasarana
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>
                        Pengadaan barang & fasilitas
                      </Typography>
                    </Box>
                  </MenuItem>

                  <Divider sx={{ my: 0.5, borderColor: "#f3f4f6" }} />

                  {/* ATK */}
                  <MenuItem
                    onClick={() => { navigate("/user/atk"); setPengajuanAnchor(null); }}
                    sx={{
                      borderRadius: "8px", py: 1, px: 1.5, gap: 1.5,
                      backgroundColor: isActive("/user/atk") ? "#fff7ed" : "transparent",
                      "&:hover": { backgroundColor: "#fff7ed" },
                    }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "8px", backgroundColor: "#fff7ed", border: "1px solid #fdba74", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <EditNoteIcon sx={{ fontSize: 17, color: "#ea580c" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600, color: "#111827", lineHeight: 1.2 }}>
                        ATK
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>
                        Alat Tulis Kantor
                      </Typography>
                    </Box>
                  </MenuItem>
                </Box>
              </Menu>

            </Box>
          )}

          {/* ── Tombol Buat Pengajuan (Desktop) — Dropdown ── */}
          {!isMobile && (
            <>
              <Box
                onClick={(e) => setBuatAnchor(e.currentTarget)}
                sx={{
                  px: 2, py: 0.9, borderRadius: "8px",
                  backgroundColor: "#111827", color: "#fff",
                  fontSize: "0.845rem", fontWeight: 600,
                  cursor: "pointer", mr: 1,
                  display: "flex", alignItems: "center", gap: 0.5,
                  transition: "all 0.15s",
                  "&:hover": { backgroundColor: "#1f2937" },
                }}
              >
                Buat Pengajuan
                <KeyboardArrowDownIcon sx={{
                  fontSize: 16,
                  transition: "transform 0.2s",
                  transform: Boolean(buatAnchor) ? "rotate(180deg)" : "rotate(0deg)",
                }} />
              </Box>

              <Menu
                anchorEl={buatAnchor}
                open={Boolean(buatAnchor)}
                onClose={() => setBuatAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    mt: 1, minWidth: 230, borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                    overflow: "hidden",
                  },
                }}
              >
                <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", px: 0.5 }}>
                    Pilih Jenis Pengajuan
                  </Typography>
                </Box>
                <Box sx={{ p: 0.75 }}>
                  <MenuItem
                    onClick={() => { navigate("/user/procurement/create"); setBuatAnchor(null); }}
                    sx={{ borderRadius: "8px", py: 1, px: 1.5, gap: 1.5, "&:hover": { backgroundColor: "#f0f7ff" } }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "8px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <AssignmentOutlinedIcon sx={{ fontSize: 17, color: "#2563eb" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600, color: "#111827", lineHeight: 1.2 }}>Sarana Prasarana</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>Pengadaan barang & fasilitas</Typography>
                    </Box>
                  </MenuItem>

                  <Divider sx={{ my: 0.5, borderColor: "#f3f4f6" }} />

                  <MenuItem
                    onClick={() => { navigate("/user/atk/create"); setBuatAnchor(null); }}
                    sx={{ borderRadius: "8px", py: 1, px: 1.5, gap: 1.5, "&:hover": { backgroundColor: "#fff7ed" } }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "8px", backgroundColor: "#fff7ed", border: "1px solid #fdba74", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <EditNoteIcon sx={{ fontSize: 17, color: "#ea580c" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.845rem", fontWeight: 600, color: "#111827", lineHeight: 1.2 }}>ATK</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>Alat Tulis Kantor</Typography>
                    </Box>
                  </MenuItem>
                </Box>
              </Menu>
            </>
          )}

          {/* Bell notifikasi */}
          <IconButton onClick={handleNotifOpen} size="small" sx={{
            color: "#374151", border: "1px solid #e5e7eb", borderRadius: "8px", p: 0.75, mr: 0.5,
            "&:hover": { backgroundColor: "#f3f4f6" },
            backgroundColor: notifOpen ? "#f0f7ff" : "transparent",
          }}>
            <Badge badgeContent={unreadCount} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 16, height: 16 } }}>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>

          <Avatar onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ width: 34, height: 34, fontSize: "0.72rem", fontWeight: 800, background: "linear-gradient(135deg, #1677ff 0%, #7c3aed 100%)", cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s", "&:hover": { transform: "scale(1.06)", boxShadow: "0 4px 14px rgba(22,119,255,0.35)" } }}>
            {loggingOut ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : userInitials}
          </Avatar>
        </Box>

        {/* Profile Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ elevation: 0, sx: { mt: 1.5, minWidth: 200, borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "0 8px 32px rgba(0,0,0,0.1)", overflow: "hidden", "& .MuiMenuItem-root": { fontSize: "0.875rem", gap: 1.5, py: 1, px: 1.5 } }}}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f3f4f6" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.875rem", color: "#111827", letterSpacing: "-0.02em" }}>{user?.name || "User"}</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>{user?.unit?.name || user?.email || "—"}</Typography>
          </Box>
          <Box sx={{ p: 0.5 }}>
            {/* ── Profile link — navigasi ke /user/profile ── */}
            <MenuItem
              onClick={() => { setAnchorEl(null); navigate("/user/profile"); }}
              sx={{ borderRadius: "8px", "&:hover": { backgroundColor: "#f0f7ff" } }}
            >
              <PersonOutlineIcon fontSize="small" sx={{ color: "#6b7280" }} /> Profile
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); navigate("/"); }} sx={{ borderRadius: "8px", "&:hover": { backgroundColor: "#f9fafb" } }}>
              <HomeOutlinedIcon fontSize="small" sx={{ color: "#6b7280" }} /> Beranda
            </MenuItem>
          </Box>
        </Menu>
      </Box>

      {/* ── CONTENT ── */}
      <Box sx={{ flexGrow: 1, py: { xs: 3, md: 4 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
          <Outlet />
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: "1px solid #e5e7eb", backgroundColor: "#fff", py: 2, textAlign: "center" }}>
        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>
          © {new Date().getFullYear()} BAS-LPKIA · Designed & Developed by{" "}
          <Box component="a" href="https://robb-main.vercel.app" target="_blank" rel="noopener noreferrer" sx={{ color: "#6b7280", fontWeight: 600, textDecoration: "none", "&:hover": { color: "#111827", textDecoration: "underline" } }}>
            Robi Permana
          </Box>
        </Typography>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#111827" }}>
          Konfirmasi Logout
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: "#4b5563" }}>
            Apakah Anda yakin ingin keluar dari sistem?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            sx={{ textTransform: "none", color: "#6b7280", fontWeight: 600 }}
          >
            Batal
          </Button>
          <Button
            onClick={confirmLogout}
            variant="contained"
            color="error"
            disabled={loggingOut}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
          >
            {loggingOut ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Ya, Logout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
